import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import useGlobalNotification from './useGlobalNotification';
import useNavigateWithSearch from './useNavigateWithSearch';
import useTranslation from '@hooks/useTranslation';
import { importRecipeFromContentsAction } from '@src/app/reducers/workspace/importRecipeFromContentsReducer';


type DownloadOptions = {
  onSuccess?: () => void;
  onError?: () => void;
  onFinally?: () => void;
}

/**
 * Downloads a kemu recipe from a url and opens it.
 * @returns a function that can be used to download a recipe from a url and open it.
 */
const useLoadRecipeFromUrl = () => {
  const dispatch = useDispatch();
  const notification = useGlobalNotification();
  const t = useTranslation('LoadRecipeFromUrl');
  const navigateWithSearch = useNavigateWithSearch();

  const downloadRecipe = useCallback(async (url: string, options?: DownloadOptions) => {
    try {
      const result = await fetch(url);
      if (!result.ok) {
        throw new Error(result.statusText);
      }

      const contents = await result.text();
      dispatch(importRecipeFromContentsAction({
        contents,
        navigate: navigateWithSearch.navigate
      }));

      options?.onSuccess?.();
    } catch (error) {
      console.log('Error fetching recipe data: ', error);
      notification.showNotification({
        title: t('Error.Title'),
        message: t('Error.Message'),
        type: 'error',
      });

      options?.onError?.();
    } finally {
      options?.onFinally?.();
    }
  }, [dispatch, notification, t, navigateWithSearch]);

  return downloadRecipe;
};

export default useLoadRecipeFromUrl;
